/**
 * FeatureFlagsContext — real-time subscription to settings/featureFlags.
 * Auto-seeds defaults on first read if the doc doesn't exist yet.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { withTenant } from '../services/data';
import { DEFAULT_FLAGS } from '../lib/featureFlags';

const FeatureFlagsContext = createContext({ flags: DEFAULT_FLAGS, loaded: false });

export function FeatureFlagsProvider({ children }) {
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const ref = doc(db, 'settings', 'featureFlags');
    return onSnapshot(ref, async (snap) => {
      if (!snap.exists()) {
        // Auto-seed on first read
        try {
          await setDoc(ref, withTenant('settings', {
            ...DEFAULT_FLAGS,
            updatedAt: serverTimestamp(),
            updatedBy: { uid: 'system', displayName: 'Auto-seed' },
          }));
        } catch (e) {
          // Non-admin reads may hit rules; fall back silently
          console.warn('featureFlags auto-seed failed', e?.message);
        }
        setFlags(DEFAULT_FLAGS);
        setLoaded(true);
        return;
      }
      const data = snap.data();
      // Merge with defaults so new keys always have a sane baseline
      const merged = { ...DEFAULT_FLAGS };
      Object.keys(DEFAULT_FLAGS).forEach((k) => {
        if (data[k]) merged[k] = data[k];
      });
      setFlags(merged);
      setLoaded(true);
    }, (err) => {
      // RW-477: permission-denied is expected during auth transitions (boot, sign-out, token refresh) — silence it.
      if (err?.code !== 'permission-denied') {
        console.warn('featureFlags read failed, using defaults', err?.message);
      }
      setFlags(DEFAULT_FLAGS);
      setLoaded(true);
    });
  }, []);

  return (
    <FeatureFlagsContext.Provider value={{ flags, loaded }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}
